import React, { useState, useMemo } from "react"
import { motion } from "framer-motion"
import {AlertTriangle, Trash2, Eye, User, DollarSign, Printer, CheckCircle, FileText, Calendar} from 'lucide-react'
import { useGuias } from "../context/GuiasContext"
import toast from "react-hot-toast"
import QRCodeLib from "qrcode"
import { gerarPixPayload } from "../utils/pixQrCode"

export default function PagamentosExpirados() {
  const { pagamentos, deletePagamento, dadosPrefeitura, marcarComoPago } = useGuias()
  const [pagamentoSelecionado, setPagamentoSelecionado] = useState<any>(null)
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>("")
  const [modalAprovar, setModalAprovar] = useState<{ aberto: boolean; pagamento: any | null }>({
    aberto: false,
    pagamento: null
  })
  const [dataPagamentoSelecionada, setDataPagamentoSelecionada] = useState<string>(
    new Date().toISOString().split("T")[0]
  )
  const [modalDeletar, setModalDeletar] = useState<{ aberto: boolean; pagamento: any | null }>({
    aberto: false,
    pagamento: null
  })

  // Filtrar apenas pagamentos expirados (pendentes com data de expiração passada)
  const pagamentosExpirados = useMemo(() => {
    const agora = new Date()
    const filtrados = pagamentos.filter(p => {
      // Apenas pagamentos pendentes
      if (p.statusPagamento !== "pendente") return false
      
      // Se não tem data de expiração, não está expirado
      if (!p.dataExpiracao) return false
      
      // Comparar data de expiração (qualquer hora do dia da expiração é considerado expirado)
      const expiracao = new Date(p.dataExpiracao)
      return expiracao.getTime() <= agora.getTime()
    })
    
    return filtrados
  }, [pagamentos])

  // Calcular total de pagamentos expirados
  const totalExpirado = useMemo(() => {
    return pagamentosExpirados.reduce((acc, p) => {
      const valor = parseFloat(p.valor.toString().replace(',', '.'))
      return acc + valor
    }, 0)
  }, [pagamentosExpirados])

  const handleAbrirModalDeletar = (pagamento: any) => {
    setModalDeletar({ aberto: true, pagamento })
  }

  const handleConfirmarDeletar = async () => {
    if (modalDeletar.pagamento) {
      await deletePagamento(modalDeletar.pagamento._id)
      setModalDeletar({ aberto: false, pagamento: null })
    }
  }

  const handleAbrirModalAprovar = (pagamento: any) => {
    // Pre-preencher com a data de vencimento como sugestão inicial
    const dataVencimento = pagamento.dataExpiracao
      ? new Date(pagamento.dataExpiracao).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0]
    setDataPagamentoSelecionada(dataVencimento)
    setModalAprovar({ aberto: true, pagamento })
  }

  const handleConfirmarAprovacao = async () => {
    if (modalAprovar.pagamento) {
      await marcarComoPago(modalAprovar.pagamento._id, dataPagamentoSelecionada)
      setModalAprovar({ aberto: false, pagamento: null })
      // Fechar modal de visualização também, se aberto
      if (pagamentoSelecionado?._id === modalAprovar.pagamento._id) {
        handleFecharComprovante()
      }
    }
  }

  const handleVisualizarComprovante = async (pagamento: any) => {
    setPagamentoSelecionado(pagamento)
    
    // Gerar QR Code
    const pixPayload = gerarPixPayload(
      dadosPrefeitura?.chavePix || "",
      (dadosPrefeitura?.nomePrefeitura || "Prefeitura").substring(0, 25),
      "Sao Jose do Serido",
      pagamento.valor,
      pagamento.numeroComprovante
    )
    
    try {
      const qrCodeUrl = await QRCodeLib.toDataURL(pixPayload, {
        width: 180,
        margin: 1,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      })
      setQrCodeDataUrl(qrCodeUrl)
    } catch (error) {
      console.error("Erro ao gerar QR Code:", error)
      toast.error("Erro ao gerar QR Code")
    }
  }

  const handleFecharComprovante = () => {
    setPagamentoSelecionado(null)
    setQrCodeDataUrl("")
  }

  const handleImprimirLista = async () => {
    try {
      const novaAba = window.open("", "_blank")
      if (!novaAba) {
        toast.error("Bloqueador de pop-up ativo. Permita pop-ups para imprimir.")
        return
      }

      const linhasTabela = pagamentosExpirados.map(pagamento => `
        <tr>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${pagamento.numeroComprovante}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${pagamento.nomePagador}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${pagamento.cpfPagador}</td>
          <td style="padding: 8px; border: 1px solid #ddd;">${pagamento.referenciaPagamento}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: right;">R$ ${pagamento.valor}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${pagamento.dataExpiracao ? new Date(pagamento.dataExpiracao).toLocaleDateString("pt-BR") : "N/A"}</td>
          <td style="padding: 8px; border: 1px solid #ddd; text-align: center;">${new Date(pagamento.createdAt).toLocaleDateString("pt-BR")}</td>
        </tr>
      `).join('')

      novaAba.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Relatório de Pagamentos Expirados</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; background: #fff; }
            .cabecalho { display: flex; align-items: center; padding: 20px; border-bottom: 3px solid #333; margin-bottom: 20px; }
            .logo-prefeitura { width: 80px; height: 80px; margin-right: 20px; }
            .dados-prefeitura h1 { font-size: 18px; font-weight: bold; color: #004161; margin-bottom: 6px; text-transform: uppercase; }
            .dados-prefeitura p { font-size: 10px; color: #333; margin: 2px 0; }
            .titulo { background: #dc2626; color: white; padding: 15px; text-align: center; margin-bottom: 20px; border-radius: 8px; }
            .titulo h2 { font-size: 20px; font-weight: bold; margin-bottom: 5px; }
            .titulo p { font-size: 12px; opacity: 0.9; }
            .info-relatorio { background: #fee; border: 2px solid #dc2626; padding: 12px; margin-bottom: 20px; border-radius: 8px; }
            .info-relatorio p { font-size: 11px; color: #991b1b; margin: 4px 0; }
            .info-relatorio .total { font-size: 16px; font-weight: bold; color: #dc2626; margin-top: 8px; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
            th { background: #f3f4f6; padding: 10px 8px; border: 1px solid #ddd; font-size: 11px; font-weight: bold; color: #333; text-align: left; }
            td { padding: 8px; border: 1px solid #ddd; font-size: 10px; color: #333; }
            .rodape { margin-top: 30px; padding-top: 15px; border-top: 2px solid #ddd; text-align: center; }
            .rodape p { font-size: 9px; color: #666; margin: 3px 0; }
            @media print {
              body { padding: 10px; }
              @page { margin: 15mm; }
            }
          </style>
        </head>
        <body>
          <div class="cabecalho">
            <img src="${dadosPrefeitura?.logoUrl || 'https://lumi.new/lumi.ing/logo.png'}" alt="Logo" class="logo-prefeitura" />
            <div class="dados-prefeitura">
              <h1>${dadosPrefeitura?.nomePrefeitura || 'Prefeitura Municipal'}</h1>
              <p><strong>CNPJ:</strong> ${dadosPrefeitura?.cnpj || 'Não informado'}</p>
              <p><strong>Endereço:</strong> ${dadosPrefeitura?.endereco || 'Não informado'}</p>
              <p><strong>CEP:</strong> ${dadosPrefeitura?.cep || 'Não informado'} | <strong>Tel:</strong> ${dadosPrefeitura?.telefone || 'Não informado'}</p>
              <p><strong>Site:</strong> https://saojosedoserido.rn.gov.br/</p>
            </div>
          </div>
          
          <div class="titulo">
            <h2>RELATÓRIO DE PAGAMENTOS EXPIRADOS</h2>
            <p>Pagamentos vencidos que necessitam renovação</p>
          </div>
          
          <div class="info-relatorio">
            <p><strong>Data de Emissão:</strong> ${new Date().toLocaleDateString("pt-BR")} às ${new Date().toLocaleTimeString("pt-BR")}</p>
            <p><strong>Total de Pagamentos Expirados:</strong> ${pagamentosExpirados.length} pagamento${pagamentosExpirados.length !== 1 ? 's' : ''}</p>
            <p class="total">VALOR TOTAL: R$ ${totalExpirado.toFixed(2).replace('.', ',')}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th style="text-align: center;">Nº Comprovante</th>
                <th>Pagador</th>
                <th style="text-align: center;">CPF/CNPJ</th>
                <th>Referência</th>
                <th style="text-align: right;">Valor</th>
                <th style="text-align: center;">Vencido em</th>
                <th style="text-align: center;">Data Emissão</th>
              </tr>
            </thead>
            <tbody>
              ${linhasTabela}
            </tbody>
          </table>
          
          <div class="rodape">
            <p><strong>Sistema EMIT - ${dadosPrefeitura?.nomePrefeitura || 'Prefeitura Municipal'}</strong></p>
            <p>Relatório gerado automaticamente pelo Sistema de Gerenciamento de Pagamentos</p>
            <p>Este documento é válido sem assinatura</p>
          </div>
          
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
        </html>
      `)
      novaAba.document.close()
    } catch (error) {
      console.error("Erro ao gerar relatório:", error)
      toast.error("Erro ao gerar relatório para impressão")
    }
  }

  const handleImprimirNovaAba = async (pagamento: any) => {
    try {
      const pixPayload = gerarPixPayload(
        dadosPrefeitura?.chavePix || "",
        (dadosPrefeitura?.nomePrefeitura || "Prefeitura").substring(0, 25),
        "Sao Jose do Serido",
        pagamento.valor,
        pagamento.numeroComprovante
      )
      
      const qrCodeUrl = await QRCodeLib.toDataURL(pixPayload, {
        width: 180,
        margin: 1,
        color: {
          dark: "#000000",
          light: "#FFFFFF"
        }
      })

      const statusHtml = `
        <div class="status-expirado">
          <p>⚠ QR CODE EXPIRADO</p>
          <p style="font-size: 10px;">Este pagamento não é mais válido. Gere um novo talão.</p>
        </div>
      `

      const novaAba = window.open("", "_blank")
      if (!novaAba) {
        toast.error("Bloqueador de pop-up ativo. Permita pop-ups para imprimir.")
        return
      }

      novaAba.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Talão PIX ${pagamento.numeroComprovante}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            body { font-family: Arial, sans-serif; padding: 20px; background: #fff; }
            .talao { max-width: 800px; margin: 0 auto; background: white; border: 2px solid #333; }
            
            .cabecalho { display: flex; align-items: center; padding: 20px; border-bottom: 3px solid #333; }
            .logo-prefeitura { width: 100px; height: 100px; margin-right: 20px; flex-shrink: 0; }
            .dados-prefeitura { flex: 1; }
            .dados-prefeitura h1 { font-size: 20px; font-weight: bold; color: #004161; margin-bottom: 8px; text-transform: uppercase; border-bottom: 2px solid #004161; padding-bottom: 4px; }
            .dados-prefeitura p { font-size: 11px; color: #333; margin: 2px 0; line-height: 1.4; }
            
            .titulo-talao { background: #d0d0d0; padding: 12px; text-align: center; border-bottom: 2px solid #333; }
            .titulo-talao h2 { font-size: 18px; font-weight: bold; color: #333; letter-spacing: 1px; }
            
            .conteudo { display: grid; grid-template-columns: 1fr 1fr; }
            
            .coluna-esquerda { padding: 20px; border-right: 2px solid #333; }
            .coluna-esquerda h3 { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 15px; text-transform: uppercase; }
            .campo { margin-bottom: 12px; }
            .campo-label { font-size: 11px; font-weight: bold; color: #333; margin-bottom: 2px; }
            .campo-valor { font-size: 12px; color: #000; }
            
            .coluna-direita { padding: 20px; display: flex; flex-direction: column; align-items: center; justify-content: center; }
            .coluna-direita h3 { font-size: 14px; font-weight: bold; color: #333; margin-bottom: 15px; text-transform: uppercase; }
            .qr-code-box { border: 3px solid #333; padding: 10px; background: white; margin-bottom: 15px; }
            .qr-code-box img { display: block; width: 200px; height: 200px; }
            .info-pagamento { width: 100%; }
            .info-pagamento .campo { margin-bottom: 8px; text-align: center; }
            .info-pagamento .campo-label { font-size: 11px; font-weight: bold; color: #333; }
            .info-pagamento .campo-valor { font-size: 13px; color: #000; font-weight: 600; }
            
            .rodape { padding: 12px 20px; border-top: 2px solid #333; text-align: center; background: #f5f5f5; }
            .rodape p { font-size: 10px; color: #333; margin: 2px 0; }
            
            .status-expirado { background: #ffcccc; border: 2px solid #cc0000; padding: 10px; margin: 10px 20px; text-align: center; }
            .status-expirado p { color: #cc0000; font-weight: bold; font-size: 12px; margin: 4px 0; }
            
            @media print {
              body { padding: 0; background: white; }
              .talao { border: 2px solid #000; }
              @page { margin: 10mm; }
            }
          </style>
        </head>
        <body>
          <div class="talao">
            <div class="cabecalho">
              <img src="${dadosPrefeitura?.logoUrl || 'https://lumi.new/lumi.ing/logo.png'}" alt="Logo" class="logo-prefeitura" />
              <div class="dados-prefeitura">
                <h1>${dadosPrefeitura?.nomePrefeitura || 'Prefeitura Municipal'}</h1>
                <p><strong>CNPJ:</strong> ${dadosPrefeitura?.cnpj || 'Não informado'}</p>
                <p><strong>Endereço:</strong> ${dadosPrefeitura?.endereco || 'Não informado'}</p>
                <p><strong>CEP:</strong> ${dadosPrefeitura?.cep || 'Não informado'} | <strong>Tel:</strong> ${dadosPrefeitura?.telefone || 'Não informado'}</p>
                <p><strong>Site:</strong> https://saojosedoserido.rn.gov.br/</p>
              </div>
            </div>
            
            <div class="titulo-talao">
              <h2>TALÃO DE PAGAMENTO PIX</h2>
            </div>
            
            ${statusHtml}
            
            <div class="conteudo">
              <div class="coluna-esquerda">
                <h3>Dados do Pagador</h3>
                <div class="campo">
                  <div class="campo-label">Nome:</div>
                  <div class="campo-valor">${pagamento.nomePagador}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">CPF:</div>
                  <div class="campo-valor">${pagamento.cpfPagador}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Referente a:</div>
                  <div class="campo-valor">${pagamento.referenciaPagamento}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Número do Comprovante:</div>
                  <div class="campo-valor">${pagamento.numeroComprovante}</div>
                </div>
                <div class="campo">
                  <div class="campo-label">Data de Emissão:</div>
                  <div class="campo-valor">${new Date(pagamento.createdAt).toLocaleDateString("pt-BR")} às ${new Date(pagamento.createdAt).toLocaleTimeString("pt-BR")}</div>
                </div>
                ${pagamento.isNFSe ? `
                <div class="campo">
                  <div class="campo-label">NFS-e - Nota Fiscal Nº:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">${pagamento.numeroNotaFiscal}</div>
                </div>
                ` : ''}
                ${pagamento.isTLL ? `
                <div class="campo">
                  <div class="campo-label">Taxa TLL:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">R$ ${pagamento.valorTLL}</div>
                </div>
                ` : ''}
                ${pagamento.isTFF ? `
                <div class="campo">
                  <div class="campo-label">Taxa TFF:</div>
                  <div class="campo-valor" style="font-weight: bold; color: #004161;">R$ ${pagamento.valorTFF}</div>
                </div>
                ` : ''}
              </div>
              
              <div class="coluna-direita">
                <h3>Pagamento via PIX</h3>
                <div class="qr-code-box">
                  <img src="${qrCodeUrl}" alt="QR Code PIX" />
                </div>
                <div class="info-pagamento">
                  <div class="campo">
                    <div class="campo-label">CHAVE PIX:</div>
                    <div class="campo-valor" style="font-size: 10px; word-break: break-all;">${pagamento.chavePix}</div>
                  </div>
                  <div class="campo">
                    <div class="campo-label">VALOR:</div>
                    <div class="campo-valor" style="font-size: 18px; color: #004161;">R$ ${pagamento.valor}</div>
                  </div>
                  <div class="campo">
                    <div class="campo-label">VENCIMENTO:</div>
                    <div class="campo-valor">${pagamento.dataExpiracao ? new Date(pagamento.dataExpiracao).toLocaleDateString("pt-BR") : "Sem vencimento"}</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div style="padding: 12px 20px; border-top: 2px solid #333; background: #fff;">
              <p style="font-size: 9px; color: #333; font-weight: bold; margin: 0 0 6px 0; text-align: center; text-transform: uppercase;">Código PIX Copia e Cola</p>
              <div style="background: #f5f5f5; border: 1px dashed #999; border-radius: 4px; padding: 6px; margin-bottom: 8px;">
                <p style="font-size: 7px; font-family: monospace; color: #333; margin: 0; word-break: break-all; line-height: 1.3;">${pixPayload}</p>
              </div>
            </div>
            
            <div class="rodape">
              <p><strong>Escaneie o QR Code acima com o aplicativo do seu banco para realizar o pagamento via PIX.</strong></p>
              <p>Este talão comprova a geração do pagamento. Para confirmação, envie comprovante para 84 98159-6466</p>
              <p>Código de Autenticidade: ${pagamento.numeroComprovante} | Sistema EMIT - ${dadosPrefeitura?.nomePrefeitura || 'Prefeitura'}</p>
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() { window.print(); }, 500);
            }
          </script>
        </body>
        </html>
      `)
      novaAba.document.close()
    } catch (error) {
      console.error("Erro ao gerar comprovante:", error)
      toast.error("Erro ao gerar comprovante para impressão")
    }
  }

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-200"
      >
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-red-500 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-800">Pagamentos Expirados</h3>
              <p className="text-xs sm:text-sm text-gray-600">Pagamentos vencidos que precisam ser renovados</p>
            </div>
          </div>
          {pagamentosExpirados.length > 0 && (
            <button
              onClick={handleImprimirLista}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium text-sm shadow-lg"
            >
              <FileText className="w-4 h-4" />
              Imprimir Lista
            </button>
          )}
        </div>

        {/* Card de Totalizador */}
        {pagamentosExpirados.length > 0 && (
          <div className="bg-gradient-to-r from-red-50 to-orange-50 border-2 border-red-300 rounded-lg p-4 mb-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-red-500 rounded-full flex-shrink-0">
                  <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600 font-medium">Total de Pagamentos Expirados</p>
                  <p className="text-xs text-gray-500">{pagamentosExpirados.length} pagamento{pagamentosExpirados.length !== 1 ? 's' : ''} vencido{pagamentosExpirados.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="text-left sm:text-right w-full sm:w-auto">
                <p className="text-xs text-gray-500 uppercase mb-1">Valor Total</p>
                <p className="text-2xl sm:text-3xl font-bold text-red-600">R$ {totalExpirado.toFixed(2).replace('.', ',')}</p>
              </div>
            </div>
          </div>
        )}

        {pagamentosExpirados.length === 0 ? (
          <div className="text-center py-12">
            <AlertTriangle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-base sm:text-lg">Nenhum pagamento expirado</p>
            <p className="text-gray-400 text-sm mt-2 px-4">Pagamentos vencidos aparecerão aqui</p>
          </div>
        ) : (
          <>
            {/* Tabela Desktop */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Comprovante</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Pagador</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">CPF</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Referência</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Valor</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Vencido em</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Data</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {pagamentosExpirados.map((pagamento, index) => (
                    <motion.tr
                      key={pagamento._id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="px-4 py-4 text-sm text-gray-800 font-medium">{pagamento.numeroComprovante}</td>
                      <td className="px-4 py-4 text-sm text-gray-800">{pagamento.nomePagador}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{pagamento.cpfPagador}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">{pagamento.referenciaPagamento}</td>
                      <td className="px-4 py-4 text-sm text-gray-800 font-semibold">R$ {pagamento.valor}</td>
                      <td className="px-4 py-4 text-sm text-red-600 font-medium">
                        {pagamento.dataExpiracao ? new Date(pagamento.dataExpiracao).toLocaleDateString("pt-BR") : "N/A"}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {new Date(pagamento.createdAt).toLocaleDateString("pt-BR")}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          <button
                            onClick={() => handleAbrirModalAprovar(pagamento)}
                            className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-xs font-medium"
                            title="Aprovar pagamento manualmente"
                          >
                            <CheckCircle className="w-3 h-3" />
                            Aprovar
                          </button>
                          <button
                            onClick={() => handleVisualizarComprovante(pagamento)}
                            className="flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-xs"
                          >
                            <Eye className="w-3 h-3" />
                            Ver
                          </button>
                          <button
                            onClick={() => handleImprimirNovaAba(pagamento)}
                            className="flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-xs"
                          >
                            <Printer className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => handleAbrirModalDeletar(pagamento)}
                            className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-xs"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Cards Mobile/Tablet */}
            <div className="lg:hidden space-y-4">
              {pagamentosExpirados.map((pagamento, index) => (
                <motion.div
                  key={pagamento._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white border border-red-200 rounded-lg p-4 shadow-sm"
                >
                  <div className="flex justify-between items-start mb-3 pb-3 border-b border-gray-200">
                    <div>
                      <p className="text-xs text-gray-500 uppercase mb-1">Comprovante</p>
                      <p className="text-sm font-bold text-gray-800">{pagamento.numeroComprovante}</p>
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      <AlertTriangle className="w-3 h-3" />
                      VENCIDO
                    </span>
                  </div>

                  <div className="space-y-2 mb-3">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Pagador</p>
                      <p className="text-sm font-medium text-gray-800">{pagamento.nomePagador}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">CPF</p>
                        <p className="text-sm text-gray-700">{pagamento.cpfPagador}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Valor</p>
                        <p className="text-sm font-bold text-gray-800">R$ {pagamento.valor}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Referência</p>
                      <p className="text-sm text-gray-700">{pagamento.referenciaPagamento}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Data</p>
                        <p className="text-sm text-gray-700">{new Date(pagamento.createdAt).toLocaleDateString("pt-BR")}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase">Vencido em</p>
                        <p className="text-sm text-red-600 font-medium">
                          {pagamento.dataExpiracao ? new Date(pagamento.dataExpiracao).toLocaleDateString("pt-BR") : "N/A"}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 pt-3 border-t border-gray-200">
                    <button
                      onClick={() => handleAbrirModalAprovar(pagamento)}
                      className="flex items-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-xs font-medium w-full justify-center"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Aprovar Manualmente
                    </button>
                    <button
                      onClick={() => handleVisualizarComprovante(pagamento)}
                      className="flex items-center gap-1 px-3 py-2 bg-teal-100 text-teal-700 rounded-lg hover:bg-teal-200 transition-colors text-xs font-medium flex-1 justify-center min-w-[100px]"
                    >
                      <Eye className="w-4 h-4" />
                      Ver
                    </button>
                    <button
                      onClick={() => handleImprimirNovaAba(pagamento)}
                      className="flex items-center gap-1 px-3 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-xs font-medium flex-1 justify-center min-w-[100px]"
                    >
                      <Printer className="w-4 h-4" />
                      Imprimir
                    </button>
                    <button
                      onClick={() => handleAbrirModalDeletar(pagamento)}
                      className="flex items-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors text-xs font-medium w-full justify-center"
                    >
                      <Trash2 className="w-4 h-4" />
                      Apagar
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </motion.div>

      {/* Modal de Visualização */}
      {pagamentoSelecionado && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={handleFecharComprovante}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-gradient-to-r from-red-600 to-red-700 p-4 rounded-t-xl flex justify-between items-center">
              <h3 className="text-lg font-bold text-white">Comprovante Expirado</h3>
              <button
                onClick={handleFecharComprovante}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="p-6">
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 shadow-2xl">
                <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-t-lg p-4 -mx-6 -mt-6 mb-4">
                  <div className="text-center text-white">
                    <h2 className="text-xl font-bold mb-1">PAGAMENTO EXPIRADO</h2>
                    <p className="text-sm opacity-90">Este QR Code não é mais válido</p>
                  </div>
                </div>

                <div className="bg-red-100 border-2 border-red-500 rounded-lg p-4 mb-4">
                  <div className="flex items-center justify-center gap-2">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                    <p className="text-red-800 font-bold uppercase">QR CODE EXPIRADO</p>
                  </div>
                  <p className="text-sm text-red-700 text-center mt-2">
                    Vencido em: {pagamentoSelecionado.dataExpiracao ? new Date(pagamentoSelecionado.dataExpiracao).toLocaleDateString("pt-BR") : "N/A"}
                  </p>
                </div>

                <div className="bg-white rounded-lg p-3 mb-3 shadow-md">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-500 uppercase">Número</p>
                      <p className="text-base font-bold text-gray-800">{pagamentoSelecionado.numeroComprovante}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 uppercase">Data e Hora</p>
                      <p className="text-sm font-semibold text-gray-800">{new Date(pagamentoSelecionado.createdAt).toLocaleDateString("pt-BR")}</p>
                      <p className="text-xs text-gray-600">{new Date(pagamentoSelecionado.createdAt).toLocaleTimeString("pt-BR")}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg p-4 mb-3 text-center">
                  <p className="text-white text-sm uppercase mb-1">Valor da Transação</p>
                  <p className="text-white text-3xl font-bold">R$ {pagamentoSelecionado.valor}</p>
                </div>

                <div className="bg-white rounded-lg p-4 mb-3 shadow-md">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <User className="w-5 h-5 text-red-600" />
                        PAGADOR
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Nome</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.nomePagador}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">CPF</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.cpfPagador}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Referente a</p>
                          <p className="text-sm font-semibold text-gray-800">{pagamentoSelecionado.referenciaPagamento}</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-red-600" />
                        RECEBEDOR
                      </h4>
                      <div className="space-y-2">
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Beneficiário</p>
                          <p className="text-sm font-semibold text-gray-800">{dadosPrefeitura?.nomePrefeitura}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">CNPJ</p>
                          <p className="text-sm font-semibold text-gray-800">{dadosPrefeitura?.cnpj}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase">Chave PIX</p>
                          <p className="text-xs font-mono text-gray-800 break-all">{pagamentoSelecionado.chavePix}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-lg p-4 shadow-md">
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700 mb-3 uppercase">QR Code PIX (Expirado)</p>
                    {qrCodeDataUrl && (
                      <div className="inline-block border-4 border-red-600 rounded-lg p-2 bg-white opacity-50">
                        <img src={qrCodeDataUrl} alt="QR Code PIX" className="w-48 h-48 mx-auto" />
                      </div>
                    )}
                    <p className="text-xs text-red-600 mt-3 font-semibold">Este código não pode mais ser utilizado</p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => handleAbrirModalAprovar(pagamentoSelecionado)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  <CheckCircle className="w-5 h-5" />
                  Aprovar Manualmente
                </button>
                <button
                  onClick={() => handleImprimirNovaAba(pagamentoSelecionado)}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Printer className="w-5 h-5" />
                  Imprimir
                </button>
                <button
                  onClick={handleFecharComprovante}
                  className="px-6 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Fechar
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
      {/* Modal de Aprovar com Data Retroativa */}
      {modalAprovar.aberto && modalAprovar.pagamento && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
          >
            {/* Header */}
            <div className="bg-green-600 rounded-t-2xl px-6 py-4 flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <CheckCircle className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Confirmar Pagamento</h3>
                <p className="text-green-100 text-sm">Comprovante {modalAprovar.pagamento.numeroComprovante}</p>
              </div>
            </div>

            <div className="p-6">
              {/* Aviso */}
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-amber-800 font-semibold text-sm">Pagamento vencido</p>
                    <p className="text-amber-700 text-xs mt-1">
                      Informe a data real em que o pagador efetuou o pagamento. Isso permite que o registro entre na prestação de contas do mês correto.
                    </p>
                  </div>
                </div>
              </div>

              {/* Dados do pagamento */}
              <div className="bg-gray-50 rounded-xl p-4 mb-5 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Pagador</span>
                  <span className="font-semibold text-gray-800">{modalAprovar.pagamento.nomePagador}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Valor</span>
                  <span className="font-bold text-gray-800">R$ {modalAprovar.pagamento.valor}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Referência</span>
                  <span className="font-medium text-gray-800 text-right max-w-[60%]">{modalAprovar.pagamento.referenciaPagamento}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Venceu em</span>
                  <span className="font-medium text-red-600">
                    {modalAprovar.pagamento.dataExpiracao
                      ? new Date(modalAprovar.pagamento.dataExpiracao).toLocaleDateString("pt-BR")
                      : "N/A"}
                  </span>
                </div>
              </div>

              {/* Seletor de data retroativa */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  📅 Data em que o pagamento foi efetuado
                </label>
                <input
                  type="date"
                  value={dataPagamentoSelecionada}
                  max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setDataPagamentoSelecionada(e.target.value)}
                  className="w-full border-2 border-gray-200 focus:border-green-500 rounded-xl px-4 py-3 text-base font-semibold text-gray-800 outline-none transition-colors"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Selecione a data retroativa (ex: quando a pessoa realmente pagou)
                </p>
              </div>

              {/* Botões */}
              <div className="flex gap-3">
                <button
                  onClick={() => setModalAprovar({ aberto: false, pagamento: null })}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarAprovacao}
                  disabled={!dataPagamentoSelecionada}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <CheckCircle className="w-5 h-5" />
                  Confirmar Pago
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Modal de Confirmar Exclusão */}
      {modalDeletar.aberto && modalDeletar.pagamento && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-sm"
          >
            <div className="bg-red-600 rounded-t-2xl px-6 py-4 flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <Trash2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white">Excluir Pagamento</h3>
                <p className="text-red-100 text-sm">Esta ação não pode ser desfeita</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-700 text-sm mb-2">Deseja excluir permanentemente o pagamento:</p>
              <p className="font-bold text-gray-900 text-base mb-1">{modalDeletar.pagamento.numeroComprovante}</p>
              <p className="text-gray-600 text-sm mb-6">{modalDeletar.pagamento.nomePagador} &bull; R$ {modalDeletar.pagamento.valor}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setModalDeletar({ aberto: false, pagamento: null })}
                  className="flex-1 px-4 py-3 border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleConfirmarDeletar}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                  Excluir
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  )
}
